/**
 * Content and scoring for the Single Muslim Marriage Readiness Quiz (/marriage-readiness-quiz).
 * Area and stage names are also whitelisted on the server (backend/routes/quiz.py): change them in both places.
 * Photos live in frontend/public/quiz/ and are free to swap: keep the file names, or edit `photo` below.
 */

export const ANSWERS = [
  { label: 'Not yet', points: 1 },
  { label: 'Getting there', points: 2 },
  { label: 'Mostly', points: 3 },
  { label: 'Yes', points: 4 },
];

export const AREAS = [
  {
    id: 'spiritual',
    name: 'Spiritual foundation',
    emoji: '🤲',
    blurb: 'Your own relationship with Allah, apart from any spouse.',
    photo: { file: 'spiritual.jpg', alt: 'A Quran resting on a teal prayer rug' },
    questions: [
      { id: 's1', text: 'I keep a daily spiritual habit, such as prayer, Quran or dhikr, even when life is busy.' },
      { id: 's2', text: 'I know which part of my Deen I want to strengthen, and I have a plan for growth.' },
      { id: 's3', text: 'I can describe what I want my faith to look like inside a marriage.' },
    ],
    results: {
      strong: { text: 'Your faith is a steady anchor. Keep protecting your daily habits, because they will support you through a marriage as well.', action: 'Write down three things you want your home to feel like spiritually.' },
      growing: { text: 'You have a base to build on, and a little more consistency will go a long way.', action: 'Choose one small habit and keep it for 14 days, such as five minutes of Quran after Fajr.' },
      foundation: { text: 'Starting small is enough. This is where most of the growth is, and it is a beautiful place to begin.', action: 'Choose one habit you can keep every day, even if it takes two minutes, and begin tomorrow.' },
    },
  },
  {
    id: 'emotional',
    name: 'Emotional readiness',
    emoji: '💜',
    blurb: 'How you handle disagreement, disappointment and the past.',
    photo: { file: 'emotional.jpg', alt: 'A tea set and an open journal on a wooden table' },
    questions: [
      { id: 'e1', text: 'When I disagree with someone, I can stay calm and say what I need without shutting down or losing my temper.' },
      { id: 'e2', text: 'I can handle disappointment or rejection without it shaking my sense of self-worth.' },
      { id: 'e3', text: 'I have worked through (or am working through), past hurt/trauma to heal emotional wounds and be emotionally available to a spouse.' },
    ],
    results: {
      strong: { text: 'You can stay steady when things get hard. That is one of the most valuable things to bring to a marriage.', action: 'Notice what helps you stay calm, and look for emotional readiness traits in connections you meet.' },
      growing: { text: 'You are developing real self-awareness, and it shows.', action: 'Next time you disagree with someone, pause for 60 seconds before you answer.' },
      foundation: { text: 'This is the area where a little work changes a lot. Be gentle with yourself as you begin.', action: 'Write about one past experience that still affects how you react, and what you wish had been different.' },
    },
  },
  {
    id: 'financial',
    name: 'Financial readiness',
    emoji: '💰',
    blurb: 'Knowing your numbers and being able to talk about them.',
    photo: { file: 'financial.jpg', alt: 'A pink piggy bank wearing glasses next to a calculator' },
    questions: [
      { id: 'f1', text: 'I know how much I earn, spend and owe each month.' },
      { id: 'f2', text: 'I have 3 or more months of savings set aside for emergencies.' },
      { id: 'f3', text: 'I could talk openly about money, including mahr and expectations, without feeling embarrassed.' },
    ],
    results: {
      strong: { text: 'You have a clear picture of your money, which makes honest conversations so much easier.', action: 'Practice saying your numbers out loud so the conversation feels natural when it comes.' },
      growing: { text: 'You are getting there. A clearer picture of your money will give you real confidence.', action: 'Use a monthly budgeting tool to track income, spending and debt.' },
      foundation: { text: 'You are not behind, you are at the start. Money is one of the most learnable parts of readiness.', action: 'Track every dollar for seven days. It is the fastest way to see where you stand.' },
    },
  },
  {
    id: 'family',
    name: 'Family and community',
    emoji: '🤝',
    blurb: 'The people who support you, and your openness to meeting others.',
    photo: { file: 'family.jpg', alt: 'Two hands passing a small paper heart to each other' },
    questions: [
      { id: 'c1', text: 'I have a marriage support team in place (trusted family, friends, a wali or a mentor) to help me through this journey.' },
      { id: 'c2', text: 'I have spoken with my parents or family about my desire to marry, in a way that did not turn into pressure or conflict.' },
      { id: 'c3', text: 'I take part in a community, local or online, and I am open to singles events, lectures or workshops.' },
    ],
    results: {
      strong: { text: 'You have people in your corner and you are open to meeting others. That support is a real blessing.', action: 'Talk with your support team about what you are looking for before you begin your next search.' },
      growing: { text: 'You have some of this in place, and a little more support will help a lot.', action: 'Choose one person you trust and ask them for honest advice on a decision.' },
      foundation: { text: 'You do not have to do this alone. Support can be built, and a community like Sirati is a place to start.', action: 'Reach out to one person this week, such as a friend, a mentor or someone at your masjid.' },
    },
  },
  {
    id: 'clarity',
    name: 'Knowing what you want',
    emoji: '🧭',
    blurb: 'Clarity about what you need, what you offer and what you can live without.',
    photo: { file: 'clarity.jpg', alt: 'Sunlight shining through white clouds in a blue sky' },
    questions: [
      { id: 'k1', text: 'I can name my top three non-negotiables in a spouse.' },
      { id: 'k2', text: 'I know the difference between my deal-breakers and my preferences.' },
      { id: 'k3', text: 'I know what I bring to a marriage and what I still need to work on.' },
    ],
    results: {
      strong: { text: 'You know what matters to you, and that clarity will inshaAllah save you time and heartache.', action: 'Write your non-negotiables and your preferences as two separate lists to distinguish between the two.' },
      growing: { text: 'You are getting clear. Sharpening the details will make your search far more focused.', action: 'Take each deal-breaker and ask whether it is truly a deal-breaker or a preference.' },
      foundation: { text: 'Clarity comes from asking yourself the right questions, and you can start today.', action: 'Write what you need, what you want and what you could live without in a spouse.' },
    },
  },
];

export const STAGES = [
  {
    id: 'foundation',
    name: 'Laying the foundation',
    emoji: '🌱',
    min: 15,
    text: 'You are at the beginning, and that is a good place to be. The time you spend on yourself now is what makes a marriage last. You do not need to rush. Pick one area and take one small step this week.',
    share: 'I am Laying the foundation',
  },
  {
    id: 'momentum',
    name: 'Building momentum',
    emoji: '🌙',
    min: 34,
    text: 'You have real strengths, and a few areas that would benefit from attention. You are closer than you may think. Focus on your one growth area and keep going.',
    share: 'I am Building momentum',
  },
  {
    id: 'ready',
    name: 'Ready to move with intention',
    emoji: '💌',
    min: 49,
    text: 'The groundwork is in place. You know yourself, and you have the habits and support to begin with clarity. Keep growing as you move forward, because readiness is something you keep building, not something you finish.',
    share: 'I am Ready to move with intention',
  },
];

export const QUESTIONS = AREAS.flatMap((area) => area.questions.map((q) => ({ ...q, areaId: area.id })));
export const TOTAL = QUESTIONS.length;

const pointsFor = (answers, ids) => ids.reduce((sum, id) => sum + (answers[id] || 0), 0);

export const isComplete = (answers) => !!answers && QUESTIONS.every((q) => answers[q.id] >= 1 && answers[q.id] <= 4);

/** Band for one area: 3 to 12 points. */
export const bandFor = (score, count) => {
  const ratio = score / (count * 4);
  if (ratio >= 0.8) return 'strong';        // 10 to 12 of 12
  if (ratio >= 0.55) return 'growing';      // 7 to 9
  return 'foundation';                      // 3 to 6
};

/** Everything the results screens need, from the answers map { questionId: 1..4 }. */
export const scoreQuiz = (answers) => {
  const areas = AREAS.map((area) => {
    const score = pointsFor(answers, area.questions.map((q) => q.id));
    return { area, score, max: area.questions.length * 4, band: bandFor(score, area.questions.length) };
  });
  const total = areas.reduce((sum, a) => sum + a.score, 0);
  const stage = [...STAGES].reverse().find((s) => total >= s.min) || STAGES[0];

  // Every area tied for the top score is a strength and every area tied for the bottom is a growth area.
  // When all five tie there is nothing to single out, so `balanced` hides both lines.
  const top = Math.max(...areas.map((a) => a.score));
  const low = Math.min(...areas.map((a) => a.score));
  const balanced = top === low;
  const strongest = areas.filter((a) => a.score === top);
  const focus = areas.filter((a) => a.score === low);
  return { areas, total, stage, strongest, focus, balanced };
};
